import React, { useMemo } from 'react';

import moment from 'moment';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';

import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { Link, Route, Router, Switch, useLocation, useParams } from 'react-router-dom';

import { FormSubmitEvent, UnreachableFunction } from 'ui/base/types';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { BusyStore } from 'ui/base/button';
import { PasswordStore } from 'ui/base/input';
import { HistoryModalStore, Modal, ModalStore } from 'ui/base/modal';
import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page';
import { ShieldIcon, ShieldSize } from 'ui/base/shield_icon/shield_icon';

import { ActiveButton, DefaultButton } from 'ui/public/button';
import { LabeledInput, PasswordInput } from 'ui/public/input';
import { ErrorMessage } from 'ui/public/error_message/error_message';

import { bytesToSize } from 'services/base/bytes';
import { Nullable, Optional, ResultKind } from 'services/base/types';
import { GlobalLocaleStore, localeToLanguageCode } from 'services/base/i18n';
import { IAccountsDomain, Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import {
  IPangolinDomain,
  UpdateHistoryRef,
  UpdateHistoryType,
  UpdateRelease,
  UpdateSnapshot,
  UpdateRestore,
  UpdateRestoreStatus,
  UpdateUpgrade,
  UpdateUpgradeStatus,
} from 'services/pangolin/types';

import { usePangolinDomain } from 'services/pangolin/pangolin_domain';
import { UpdateSnapshotService } from 'services/pangolin/update_snapshot_service';
import { UpdateUpgradeService } from 'services/pangolin/update_upgrade_service';
import { UpdateRestoreService } from 'services/pangolin/update_restore_service';

import { Redirect } from 'pages/core/redirect/redirect';

import { UpdateHistoryModal } from './modals/update_history_modal';
import { UpdateRestoreModal } from './modals/update_restore_modal';
import { UpdateUpgradeModal } from './modals/update_upgrade_modal';

import { UpdateUpgradeStepper } from './ui/update_upgrade_stepper';
import { UpdateRestoreStepper } from './ui/update_restore_stepper';

import baseStyles from './manage_updates.module.css';
import Messages from './manage_updates_messages';

import heroIconImageWarningMidnight from './assets/heroes/warning/dark.png';
import heroIconImageWarningDaylight from './assets/heroes/warning/light.png';

import fieldIconImageModifiedMidnight from './assets/fields/modified/dark.png';
import fieldIconImageModifiedDaylight from './assets/fields/modified/light.png';
import fieldIconImageRestorekMidnight from './assets/fields/restore/dark.png';
import fieldIconImageRestoreDaylight from './assets/fields/restore/light.png';
import fieldIconImageUpgradeMidnight from './assets/fields/upgrade/dark.png';
import fieldIconImageUpgradeDaylight from './assets/fields/upgrade/light.png';
import fieldIconImageVersionMidnight from './assets/fields/version/dark.png';
import fieldIconImageVersionDaylight from './assets/fields/version/light.png';
import fieldIconImageSourceMidnight from './assets/fields/source/dark.png';
import fieldIconImageSourceDaylight from './assets/fields/source/light.png';
import fieldIconImageTargetMidnight from './assets/fields/target/dark.png';
import fieldIconImageTargetDaylight from './assets/fields/target/light.png';

export type ManageUpdatesStyles = {
  header: string;
  headerItem: string;
  headerText: string;
  headerButton: string;
  headerButtonIcon: string;

  pangolin: string;
  pangolinIcon: string;
  pangolinText: string;
  pangolinTitle: string;
  pangolinSubtitle: string;

  hero: string;
  heroIcon: string;
  heroGlow: string;
  heroHead: string;
  heroTitle: string;
  heroText: string;
  heroDetail: string;

  content: string;
  section: string;
  sectionTitle: string;
  row: string;
  column: string;
  label: string;
  value: string;

  field: string;
  fieldPart: string;
  fieldIcon: string;
  fieldAlert: string;
  fieldTextLeft: string;
  fieldTextRight: string;
  fieldCorpusLeft: string;
  fieldCorpusRight: string;
  fieldInfo: string;
  fieldInfoLong: string;

  modalContainer: string;
  modalContainerBig: string;
  modalContainerLarge: string;
  modalContainerLargest: string;
  modalContainerPopOut: string;
  modalHeaderRow: string;
  modalContentRow: string;
  modalCloseIcon: string;

  clickable: string;
  disabled: string;

  item: string;
  notes: string;
  corpus: string;

  alertCritical: string;
  alertWarning: string;

  open: string;
  delete: string;
  pad: string;

  heroIconImageWarning: string;

  fieldIconImageModified: string;
  fieldIconImageVersion: string;
  fieldIconImageUpgrade: string;
  fieldIconImageRestore: string;
  fieldIconImageSource: string;
  fieldIconImageTarget: string;
};

export const themedStyles: ThemedStyles<ManageUpdatesStyles> = {
  [Theme.DAYLIGHT]: {
    header: baseStyles.header,
    headerItem: baseStyles.headerItem,
    headerText: baseStyles.daylightHeaderText,
    headerButton: baseStyles.headerButton,
    headerButtonIcon: baseStyles.daylightHeaderButtonIcon,

    pangolin: baseStyles.pangolin,
    pangolinIcon: baseStyles.pangolinIcon,
    pangolinText: baseStyles.pangolinText,
    pangolinTitle: baseStyles.pangolinTitle,
    pangolinSubtitle: baseStyles.pangolinSubtitle,

    hero: baseStyles.hero,
    heroIcon: baseStyles.heroIcon,
    heroGlow: baseStyles.herGlow,
    heroHead: baseStyles.heroHead,
    heroTitle: baseStyles.heroTitle,
    heroText: baseStyles.heroText,
    heroDetail: baseStyles.heroDetail,

    content: baseStyles.content,
    section: baseStyles.section,
    sectionTitle: baseStyles.daylightSectionTitle,
    row: baseStyles.row,
    column: baseStyles.column,
    label: baseStyles.daylightLabel,
    value: baseStyles.value,

    field: baseStyles.field,
    fieldPart: baseStyles.fieldPart,
    fieldIcon: baseStyles.fieldIcon,
    fieldAlert: baseStyles.fieldAlert,
    fieldTextLeft: baseStyles.fieldTextLeft,
    fieldTextRight: baseStyles.fieldTextRight,
    fieldCorpusLeft: baseStyles.fieldCorpusLeft,
    fieldCorpusRight: baseStyles.fieldCorpusRight,
    fieldInfo: baseStyles.fieldInfo,
    fieldInfoLong: baseStyles.fieldInfoLong,

    modalContainer: baseStyles.modalContainer,
    modalContainerBig: baseStyles.modalContainerBig,
    modalContainerLarge: baseStyles.modalContainerLarge,
    modalContainerLargest: baseStyles.modalContainerLargest,
    modalContainerPopOut: baseStyles.modalContainerPopOut,
    modalHeaderRow: baseStyles.modalHeaderRow,
    modalContentRow: baseStyles.modalContentRow,
    modalCloseIcon: baseStyles.daylightModalCloseIcon,

    clickable: baseStyles.clickable,
    disabled: baseStyles.disabled,

    item: baseStyles.daylightItem,
    notes: baseStyles.notes,
    corpus: baseStyles.daylightCorpus,

    alertCritical: baseStyles.alertCritical,
    alertWarning: baseStyles.alertWarning,

    open: baseStyles.daylightOpen,
    delete: baseStyles.daylightDelete,
    pad: baseStyles.pad,

    heroIconImageWarning: heroIconImageWarningDaylight,

    fieldIconImageModified: fieldIconImageModifiedDaylight,
    fieldIconImageVersion: fieldIconImageVersionDaylight,
    fieldIconImageUpgrade: fieldIconImageUpgradeDaylight,
    fieldIconImageRestore: fieldIconImageRestoreDaylight,
    fieldIconImageSource: fieldIconImageSourceDaylight,
    fieldIconImageTarget: fieldIconImageTargetDaylight,
  },
  [Theme.MIDNIGHT]: {
    header: baseStyles.header,
    headerItem: baseStyles.headerItem,
    headerText: baseStyles.midnightHeaderText,
    headerButton: baseStyles.headerButton,
    headerButtonIcon: baseStyles.midnightHeaderButtonIcon,

    pangolin: baseStyles.pangolin,
    pangolinIcon: baseStyles.pangolinIcon,
    pangolinText: baseStyles.pangolinText,
    pangolinTitle: baseStyles.pangolinTitle,
    pangolinSubtitle: baseStyles.pangolinSubtitle,

    hero: baseStyles.hero,
    heroIcon: baseStyles.heroIcon,
    heroGlow: baseStyles.herGlow,
    heroHead: baseStyles.heroHead,
    heroTitle: baseStyles.heroTitle,
    heroText: baseStyles.heroText,
    heroDetail: baseStyles.heroDetail,

    content: baseStyles.content,
    section: baseStyles.section,
    sectionTitle: baseStyles.midnightSectionTitle,
    row: baseStyles.row,
    column: baseStyles.column,
    label: baseStyles.midnightLabel,
    value: baseStyles.value,

    field: baseStyles.field,
    fieldPart: baseStyles.fieldPart,
    fieldIcon: baseStyles.fieldIcon,
    fieldAlert: baseStyles.fieldAlert,
    fieldTextLeft: baseStyles.fieldTextLeft,
    fieldTextRight: baseStyles.fieldTextRight,
    fieldCorpusLeft: baseStyles.fieldCorpusLeft,
    fieldCorpusRight: baseStyles.fieldCorpusRight,
    fieldInfo: baseStyles.fieldInfo,
    fieldInfoLong: baseStyles.fieldInfoLong,

    modalContainer: baseStyles.modalContainer,
    modalContainerBig: baseStyles.modalContainerBig,
    modalContainerLarge: baseStyles.modalContainerLarge,
    modalContainerLargest: baseStyles.modalContainerLargest,
    modalContainerPopOut: baseStyles.modalContainerPopOut,
    modalHeaderRow: baseStyles.modalHeaderRow,
    modalContentRow: baseStyles.modalContentRow,
    modalCloseIcon: baseStyles.midnightModalCloseIcon,

    clickable: baseStyles.clickable,
    disabled: baseStyles.disabled,

    item: baseStyles.midnightItem,
    notes: baseStyles.notes,
    corpus: baseStyles.midnightCorpus,

    alertCritical: baseStyles.alertCritical,
    alertWarning: baseStyles.alertWarning,

    open: baseStyles.midnightOpen,
    delete: baseStyles.midnightDelete,
    pad: baseStyles.pad,

    heroIconImageWarning: heroIconImageWarningMidnight,

    fieldIconImageModified: fieldIconImageModifiedMidnight,
    fieldIconImageVersion: fieldIconImageVersionMidnight,
    fieldIconImageUpgrade: fieldIconImageUpgradeMidnight,
    fieldIconImageRestore: fieldIconImageRestorekMidnight,
    fieldIconImageSource: fieldIconImageSourceMidnight,
    fieldIconImageTarget: fieldIconImageTargetMidnight,
  },
};

type VersionInfoModalProps = { styles: ManageUpdatesStyles; release: UpdateRelease; modal: ModalStore; };
const VersionInfoModal = observer(({ styles, release, modal }: VersionInfoModalProps) => {
  return (
    <Modal store={modal}>
      <div className={classNames(styles.modalContainerBig, { [styles.modalContainerPopOut]: modal.visible })}>
        <StandardPage>
          <div className={styles.modalHeaderRow}>
            {Messages.versionInfoTitle()}
            <button
              className={styles.modalCloseIcon}
              onClick={modal.hideModal}
              type='button'
            />
          </div>
          <div className={styles.modalContentRow}>
            <div className={styles.section}>
              <div className={styles.field}>
                <div className={styles.fieldTextLeft} >
                  {release.tag}
                  <div className={styles.fieldInfo}>
                    {Messages.versionInfoFieldVersion()}
                  </div>
                </div>
                <img className={styles.fieldIcon}
                    src={styles.fieldIconImageVersion}
                    alt='Version Icon' />
              </div>
              {release?.notes && (
                <div className={styles.row}>
                  <div className={classNames(styles.column, styles.corpus)}>
                    <div className={styles.label}>
                      {Messages.versionInfoFieldNotes()}
                    </div>
                    <div className={styles.value}>
                      <ReactMarkdown>{release.notes}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className={styles.section}>
              <DefaultButton type='button' onClick={modal.hideModal}>
                {Messages.versionInfoButtonBack()}
              </DefaultButton>
            </div>
          </div>
        </StandardPage>
      </div>
    </Modal>
  );
});

type CancelUpgradeModalProps = { styles: ManageUpdatesStyles; pangolin: Pangolin; domain: IPangolinDomain; modal: ModalStore };
const CancelUpgradeModal = observer(({ styles, pangolin, domain, modal }: CancelUpgradeModalProps) => {
  const [busy] = React.useState(new BusyStore());
  const [error, setError] = React.useState(' ');

  const onSubmit = React.useCallback(async(event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const result = await busy.perform(async() => {
      try {
        modal.freezeModal();
        return await UpdateUpgradeService.cancel(domain, pangolin);
      } finally {
        modal.unfreezeModal();
      }
    });

    if (result.kind === ResultKind.OK) {
      modal.hideModal();
    } else if (result.status === 401) {
      // Display authorization error
      setError(Messages.errorUnauthorized());
    } else {
      // Display generic error
      setError(Messages.errorGeneric());
    }
  }, [modal]);

  return (
    <Modal store={modal}>
      <div className={classNames(styles.modalContainer, { [styles.modalContainerPopOut]: modal.visible })}>
        <StandardPage>
          <div className={styles.modalHeaderRow}>
            {Messages.upgradeCancelTitle()}
            <button
              className={styles.modalCloseIcon}
              onClick={modal.hideModal}
              type='button'
            />
          </div>
          <form className={styles.modalContentRow} onSubmit={onSubmit}>
            <div className={styles.hero}>
              <img className={styles.heroIcon} src={styles.heroIconImageWarning} alt='Warning Icon' />
              <div className={styles.heroTitle}>
                {Messages.upgradeCancelHeroTitle()}
              </div>
              <p className={styles.heroText}>
                {Messages.upgradeCancelHeroText()}
              </p>
            </div>
            <div className={styles.section}>
              <ErrorMessage message={error}/>
              <ActiveButton busy={busy}>
                {Messages.upgradeCancelButtonConfirm()}
              </ActiveButton>
              <DefaultButton type='button' disabled={busy.value} onClick={modal.hideModal}>
                {Messages.upgradeCancelButtonBack()}
              </DefaultButton>
            </div>
          </form>
        </StandardPage>
      </div>
    </Modal>
  );
});

type CancelRestoreModalProps = { styles: ManageUpdatesStyles; pangolin: Pangolin; domain: IPangolinDomain; modal: ModalStore };
const CancelRestoreModal = observer(({ styles, pangolin, domain, modal }: CancelRestoreModalProps) => {
  const [busy] = React.useState(new BusyStore());
  const [error, setError] = React.useState(' ');

  const onSubmit = React.useCallback(async(event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const result = await busy.perform(async() => {
      try {
        modal.freezeModal();
        return await UpdateRestoreService.cancel(domain, pangolin);
      } finally {
        modal.unfreezeModal();
      }
    });

    if (result.kind === ResultKind.OK) {
      modal.hideModal();
    } else if (result.status === 401) {
      // Display authorization error
      setError(Messages.errorUnauthorized());
    } else {
      // Display generic error
      setError(Messages.errorGeneric());
    }
  }, [modal]);

  return (
    <Modal store={modal}>
      <div className={classNames(styles.modalContainer, { [styles.modalContainerPopOut]: modal.visible })}>
        <StandardPage>
          <div className={styles.modalHeaderRow}>
            {Messages.restoreCancelTitle()}
            <button
              className={styles.modalCloseIcon}
              onClick={modal.hideModal}
              type='button'
            />
          </div>
          <form className={styles.modalContentRow} onSubmit={onSubmit}>
            <div className={styles.hero}>
              <img className={styles.heroIcon} src={styles.heroIconImageWarning} alt='Warning Icon' />
              <div className={styles.heroTitle}>
                {Messages.restoreCancelHeroTitle()}
              </div>
              <p className={styles.heroText}>
                {Messages.restoreCancelHeroText()}
              </p>
            </div>
            <div className={styles.section}>
              <ErrorMessage message={error}/>
              <ActiveButton busy={busy}>
                {Messages.restoreCancelButtonConfirm()}
              </ActiveButton>
              <DefaultButton type='button' disabled={busy.value} onClick={modal.hideModal}>
                {Messages.restoreCancelButtonBack()}
              </DefaultButton>
            </div>
          </form>
        </StandardPage>
      </div>
    </Modal>
  );
});

type ManageUpdatesHeroProps = { pangolin: Pangolin; latest: Nullable<string>; current: Nullable<string> };
const ManageUpdatesHero = observer(({ pangolin, latest, current }: ManageUpdatesHeroProps) => {
  const styles = useStyles(themedStyles);

  const statusMessage = useMemo(() => {
    if (pangolin.updateActiveUpgradeID) return Messages.heroStatusUpgrading();
    if (pangolin.updateActiveRestoreID) return Messages.heroStatusRestoring();
    return latest === current ? Messages.heroStatusLatest() : Messages.heroStatusAvailable();
  }, [latest, current, pangolin.updateActiveUpgradeID, pangolin.updateActiveRestoreID]);

  return (
    <div className={styles.pangolin}>
      <ShieldIcon
        className={styles.pangolinIcon}
        colorCode={pangolin.colorCode}
        onlineStatus={pangolin.onlineStatus}
        size={ShieldSize.MEDIUM}
      />
      <div className={styles.pangolinText}>
        <div className={styles.pangolinTitle}>
          {pangolin.nickname}
        </div>
        <div className={styles.pangolinSubtitle}>
          {statusMessage}
        </div>
      </div>
    </div>
  );
});

type ManageUpdatesUpgradeInfoProps = { domain: IPangolinDomain; upgrade: UpdateUpgrade };
const ManageUpdatesUpgradeInfo = observer(({ domain, upgrade }: ManageUpdatesUpgradeInfoProps) => {
  const styles = useStyles(themedStyles);

  const [sourceModal] = React.useState(() => new ModalStore(false));
  const [targetModal] = React.useState(() => new ModalStore(false));

  const sourceRelease = (
    !upgrade.source_release_id ? undefined :
    domain.updateReleases.get(upgrade.source_release_id)
  );

  const targetRelease = (
    !upgrade.target_release_id ? undefined :
    domain.updateReleases.get(upgrade.target_release_id)
  );

  return (
    <>
      {sourceRelease && (
        <VersionInfoModal
          styles={styles}
          release={sourceRelease}
          modal={sourceModal}
        />
      )}
      {targetRelease && (
        <VersionInfoModal
          styles={styles}
          release={targetRelease}
          modal={targetModal}
        />
      )}
      <div className={styles.section}>
        {sourceRelease && (
          <div className={classNames(styles.field, styles.item, styles.clickable)}
               onClick={sourceModal.showModal}>
            <div className={styles.fieldTextLeft}>
              {sourceRelease.tag}
              <div className={styles.fieldInfo}>
                {Messages.upgradeInfoFieldSource()}
              </div>
            </div>
            <img className={styles.fieldIcon} alt='Source Icon'
                  src={styles.fieldIconImageSource} />
            <div className={styles.open} />
          </div>
        )}
        {targetRelease && (
          <div className={classNames(styles.field, styles.item, styles.clickable)}
               onClick={targetModal.showModal}>
            <div className={styles.fieldTextLeft}>
              {targetRelease.tag}
              <div className={styles.fieldInfo}>
                {Messages.upgradeInfoFieldTarget()}
              </div>
            </div>
            <img className={styles.fieldIcon} alt='Target Icon'
                src={styles.fieldIconImageTarget} />
            <div className={styles.open} />
          </div>
        )}
        <div className={classNames(styles.field, styles.item)}>
          <div className={styles.fieldTextLeft} >
            {moment(upgrade.created_at).fromNow()}
            <div className={styles.fieldInfo}>
              {Messages.upgradeInfoFieldDispatchedAt()}
            </div>
          </div>
          <img className={styles.fieldIcon} alt='Modified Icon'
              src={styles.fieldIconImageModified} />
          <div className={classNames(styles.open, styles.disabled)} />
        </div>
      </div>
    </>
  );
});

type ManageUpdatesUpgradeProgressProps = { upgrade: UpdateUpgrade };
const ManageUpdatesUpgradeProgress = observer(({ upgrade }: ManageUpdatesUpgradeProgressProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.section}>
      <UpdateUpgradeStepper upgrade={upgrade} />
    </div>
  );
});

type ManageUpdatesUpgradeActionsProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
  upgrade: UpdateUpgrade;
};
const ManageUpdatesUpgradeActions = observer(({ pangolin, domain, upgrade }: ManageUpdatesUpgradeActionsProps) => {
  const styles = useStyles(themedStyles);
  const [modal] = React.useState(() => new ModalStore(false));

  const cancellable = useMemo(() => {
    return upgrade.status === UpdateUpgradeStatus.PENDING;
  }, [upgrade.status]);

  return (
    <>
      <CancelUpgradeModal
        styles={styles}
        pangolin={pangolin}
        domain={domain}
        modal={modal}
      />
      <div className={styles.section}>
        <DefaultButton disabled={!cancellable} onClick={modal.showModal}>
          {Messages.upgradeActionsButtonCancel()}
        </DefaultButton>
      </div>
    </>
  );
});

type ManageUpdatesRestoreInfoProps = { domain: IPangolinDomain; restore: UpdateRestore };
const ManageUpdatesRestoreInfo = observer(({ domain, restore }: ManageUpdatesRestoreInfoProps) => {
  const styles = useStyles(themedStyles);

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  const [sourceModal] = React.useState(() => new ModalStore(false));
  const [targetModal] = React.useState(() => new ModalStore(false));

  const sourceRelease = (
    !restore.source_release_id ? undefined :
    domain.updateReleases.get(restore.source_release_id)
  );

  const targetRelease = (
    !restore.target_release_id ? undefined :
    domain.updateReleases.get(restore.target_release_id)
  );

  const targetSnapshot = (
    !restore.target_snapshot_id ? undefined :
    domain.updateSnapshots.get(restore.target_snapshot_id)
  );

  return (
    <>
      {sourceRelease && (
        <VersionInfoModal
          styles={styles}
          release={sourceRelease}
          modal={sourceModal}
        />
      )}
      {targetRelease && (
        <VersionInfoModal
          styles={styles}
          release={targetRelease}
          modal={targetModal}
        />
      )}
      <div className={styles.section}>
        {sourceRelease && (
          <div className={classNames(styles.field, styles.item, styles.clickable)}
               onClick={sourceModal.showModal}>
            <div className={styles.fieldTextLeft}>
              {sourceRelease.tag}
              <div className={styles.fieldInfo}>
                {Messages.restoreInfoFieldSource()}
              </div>
            </div>
            <img className={styles.fieldIcon} alt='Source Icon'
                  src={styles.fieldIconImageSource} />
            <div className={styles.open} />
          </div>
        )}
        {targetRelease && (
          <div className={classNames(styles.field, styles.item, styles.clickable)}
               onClick={targetModal.showModal}>
            <div className={styles.fieldTextLeft}>
              {targetRelease.tag}
              <div className={styles.fieldInfo}>
                {Messages.restoreInfoFieldTarget()}
              </div>
            </div>
            <img className={styles.fieldIcon} alt='Target Icon'
                src={styles.fieldIconImageTarget} />
            <div className={styles.open} />
          </div>
        )}
        {targetSnapshot && (
          <div className={classNames(styles.field, styles.item)}>
            <div className={styles.fieldTextLeft}>
              {targetSnapshot.created_at.toLocaleString(locale, format)}
              <div className={styles.fieldInfo}>
                {Messages.restoreInfoFieldDate()}
              </div>
            </div>
            <img className={styles.fieldIcon} alt='Modified Icon'
                 src={styles.fieldIconImageModified} />
            <div className={classNames(styles.open, styles.disabled)} />
          </div>
        )}
        <div className={classNames(styles.field, styles.item)}>
          <div className={styles.fieldTextLeft} >
            {moment(restore.created_at).fromNow()}
            <div className={styles.fieldInfo}>
              {Messages.restoreInfoFieldDispatchedAt()}
            </div>
          </div>
          <img className={styles.fieldIcon} alt='Modified Icon'
              src={styles.fieldIconImageModified} />
          <div className={classNames(styles.open, styles.disabled)} />
        </div>
      </div>
    </>
  );
});

type ManageUpdatesRestoreProgressProps = { restore: UpdateRestore };
const ManageUpdatesRestoreProgress = observer(({ restore }: ManageUpdatesRestoreProgressProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.section}>
      <UpdateRestoreStepper restore={restore} />
    </div>
  );
});

type ManageUpdatesRestoreActionsProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
  restore: UpdateRestore;
};
const ManageUpdatesRestoreActions = observer(({ pangolin, domain, restore }: ManageUpdatesRestoreActionsProps) => {
  const styles = useStyles(themedStyles);
  const [modal] = React.useState(() => new ModalStore(false));

  const cancellable = useMemo(() => {
    return restore.status === UpdateRestoreStatus.PENDING;
  }, [restore.status]);

  return (
    <>
      <CancelRestoreModal
        styles={styles}
        pangolin={pangolin}
        domain={domain}
        modal={modal}
      />
      <div className={styles.section}>
        <DefaultButton disabled={!cancellable} onClick={modal.showModal}>
          {Messages.restoreActionsButtonCancel()}
        </DefaultButton>
      </div>
    </>
  );
});

type ManageUpdatesDefaultInfoProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
};
const ManageUpdatesDefaultInfo = observer(({ pangolin, domain }: ManageUpdatesDefaultInfoProps) => {
  const styles = useStyles(themedStyles);

  const updateCurrentRelease = (
    !pangolin.updateActiveReleaseID ? undefined :
    domain.updateReleases.get(pangolin.updateActiveReleaseID)
  );

  const updateCurrentTag = updateCurrentRelease?.tag ?? pangolin.updateVersion ?? null;

  const updateLastHistory = (
    !domain.updateHistory.size ? undefined :
    domain.updateHistory.find(h => {
      switch (h.type) {
        case UpdateHistoryType.UPGRADE: {
          const upgrade = domain.updateUpgrades.get(h.upgrade_id);
          return upgrade?.status === UpdateUpgradeStatus.COMPLETED;
        }
        case UpdateHistoryType.RESTORE: {
          const restore = domain.updateRestores.get(h.restore_id);
          return restore?.status === UpdateRestoreStatus.COMPLETED;
        }
        default: {
          return UnreachableFunction(h);
        }
      }
    })
  );

  const updateLastTimestamp = updateLastHistory ? ((h: UpdateHistoryRef) => {
    switch (h.type) {
      case UpdateHistoryType.UPGRADE: {
        const upgrade = domain.updateUpgrades.get(h.upgrade_id);
        return upgrade?.updated_at;
      }
      case UpdateHistoryType.RESTORE: {
        const restore = domain.updateRestores.get(h.restore_id);
        return restore?.updated_at;
      }
      default: {
        return UnreachableFunction(h);
      }
    }
  })(updateLastHistory) : undefined;

  return (
    <div className={styles.section}>
      <div className={styles.field}>
        <div className={styles.fieldTextLeft} >
          {updateCurrentTag}
          <div className={styles.fieldInfo}>
            {Messages.defaultInfoFieldVersion()}
          </div>
        </div>
        <img className={styles.fieldIcon}
             src={styles.fieldIconImageVersion}
             alt='Version Icon' />
      </div>
      <div className={styles.field}>
        <div className={styles.fieldTextLeft} >
          {updateLastTimestamp ? moment(updateLastTimestamp).fromNow() :
           Messages.defaultInfoValueUpdatedAtUnknown()}
          <div className={styles.fieldInfo}>
            {Messages.defaultInfoFieldUpdatedAt()}
          </div>
        </div>
        <img className={styles.fieldIcon}
             src={styles.fieldIconImageModified}
             alt='Modified Icon' />
      </div>
      {updateCurrentRelease?.notes && (
        <div className={styles.row}>
          <div className={classNames(styles.column, styles.corpus)}>
            <div className={styles.label}>
              {Messages.defaultInfoFieldReleaseNotes()}
            </div>
            <div className={styles.value}>
              <ReactMarkdown>{updateCurrentRelease.notes}</ReactMarkdown>
              {/* TODO: remove this later when we have real data */}
              {/* <ReactMarkdown>
                {"Added support for IPv6 network configuration\n\n" +
                "* Improved VPN connection stability\n" +
                "* Enhanced firewall security rules\n" +
                "* Fixed issue with DNS resolution\n" +
                "* Updated system libraries for better performance\n" +
                "* Improved network throughput monitoring\n" +
                "* Added new parental control features\n" +
                "* Bug fixes and general stability improvements\n"}
              </ReactMarkdown> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

type ManageUpdatesActionsProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
};
const ManageUpdatesDefaultActions = observer(({ pangolin, domain }: ManageUpdatesActionsProps) => {
  const location = useLocation();
  const styles = useStyles(themedStyles);

  const release = (
    !pangolin.updateActiveReleaseID ? undefined :
    domain.updateReleases.get(pangolin.updateActiveReleaseID)
  );

  const tag = release?.tag ?? pangolin.updateVersion ?? null;

  const upgradeRoot = `${location.pathname}/upgrade`;
  const [showUpgrade] = React.useState(() => (
    new HistoryModalStore(false)
  ));
  React.useEffect(() => {
    return showUpgrade.handleGoBack();
  }, [showUpgrade]);
  const onShowUpgradeModal = React.useCallback(() => {
    showUpgrade.showModalAt(`${upgradeRoot}/releases`);
  }, [showUpgrade, upgradeRoot]);

  const restoreRoot = `${location.pathname}/restore`;
  const [showRestore] = React.useState(() => (
    new HistoryModalStore(false)
  ));
  React.useEffect(() => {
    return showRestore.handleGoBack();
  }, [showRestore]);
  const onShowRestoreModal = React.useCallback(() => {
    showRestore.showModalAt(`${restoreRoot}/snapshots`);
  }, [showRestore, restoreRoot]);

  return (
    <>
      <UpdateUpgradeModal
        pangolin={pangolin}
        domain={domain}
        version={tag}
        modal={showUpgrade}
        root={upgradeRoot}
      />
      <UpdateRestoreModal
        pangolin={pangolin}
        domain={domain}
        version={tag}
        modal={showRestore}
        root={restoreRoot}
      />
      <div className={styles.section}>
        <ActiveButton onClick={onShowUpgradeModal}>
          {Messages.defaultActionsButtonUpgrade()}
        </ActiveButton>
        <DefaultButton onClick={onShowRestoreModal}>
          {Messages.defaultActionsButtonRestore()}
        </DefaultButton>
      </div>
    </>
  );
});

type ManageUpdateHistoryItemProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
  item: UpdateHistoryRef;
  locale: string;
  format: object;
};
const ManageUpdateHistoryItem = observer(({
  pangolin,
  domain,
  item,
  locale,
  format,
}: ManageUpdateHistoryItemProps) => {
  const location = useLocation();
  const styles = useStyles(themedStyles);

  const upgrade = useMemo(() => {
    if (item.type !== UpdateHistoryType.UPGRADE) return undefined;
    return domain.updateUpgrades.get(item.upgrade_id);
  }, [item]);

  const restore = useMemo(() => {
    if (item.type !== UpdateHistoryType.RESTORE) return undefined;
    return domain.updateRestores.get(item.restore_id);
  }, [item]);

  const target: Optional<UpdateRelease> = useMemo(() => {
    switch (item.type) {
      case UpdateHistoryType.UPGRADE: {
        return (
          !upgrade?.target_release_id ? undefined :
          domain.updateReleases.get(upgrade.target_release_id)
        );
      }
      case UpdateHistoryType.RESTORE: {
        return (
          !restore?.target_release_id ? undefined :
          domain.updateReleases.get(restore.target_release_id)
        );
      }
      default: {
        return UnreachableFunction(item);
      }
    }
  }, [item, upgrade?.target_release_id, restore?.target_release_id]);

  const date = useMemo(() => {
    switch (item.type) {
      case UpdateHistoryType.UPGRADE: {
        return upgrade?.created_at;
      }
      case UpdateHistoryType.RESTORE: {
        return restore?.created_at;
      }
      default: {
        return UnreachableFunction(item);
      }
    }
  }, [item, upgrade?.created_at, restore?.created_at]);

  const alert = useMemo(() => {
    switch (item.type) {
      case UpdateHistoryType.UPGRADE:
        switch (upgrade?.status) {
          case UpdateUpgradeStatus.CANCELLED: return true;
          case UpdateUpgradeStatus.SKIPPED: return true;
          case UpdateUpgradeStatus.FAILED: return true;
          default: return false;
        }
      case UpdateHistoryType.RESTORE:
        switch (restore?.status) {
          case UpdateRestoreStatus.CANCELLED: return true;
          case UpdateRestoreStatus.SKIPPED: return true;
          case UpdateRestoreStatus.FAILED: return true;
          default: return false;
        }
      default: {
        return UnreachableFunction(item);
      }
    }
  }, [item, upgrade?.status, restore?.status]);

  const root = useMemo(() => {
    switch (item.type) {
      case UpdateHistoryType.UPGRADE: return `${location.pathname}/history/upgrade/${item.upgrade_id}`;
      case UpdateHistoryType.RESTORE: return `${location.pathname}/history/restore/${item.restore_id}`;
      default: return UnreachableFunction(item);
    }
  }, [item]);

  const [modal] = React.useState(() => new HistoryModalStore(false));
  React.useEffect(() => { return modal.handleGoBack(); }, [modal]);
  const onShowModal = React.useCallback(() => modal.showModalAt(`${root}/status`), [modal, root]);

  const itemIcon = useMemo(() => {
    switch (item.type) {
      case UpdateHistoryType.UPGRADE: return <img className={styles.fieldIcon} src={styles.fieldIconImageUpgrade} alt='Upgrade Icon' />;
      case UpdateHistoryType.RESTORE: return <img className={styles.fieldIcon} src={styles.fieldIconImageRestore} alt='Restore Icon' />;
      default: return UnreachableFunction(item);
    }
  }, [item]);

  const itemName = useMemo(() => {
    switch (item.type) {
      case UpdateHistoryType.UPGRADE: return Messages.historyItemUpgrade(target?.tag ?? '');
      case UpdateHistoryType.RESTORE: return Messages.historyItemRestore(target?.tag ?? '');
      default: return UnreachableFunction(item);
    }
  }, [item]);

  return (
    <>
      <UpdateHistoryModal
        pangolin={pangolin}
        domain={domain}
        item={item}
        locale={locale}
        format={format}
        modal={modal}
        root={root}
      />
      <div className={classNames(styles.field, styles.item, styles.clickable)} onClick={onShowModal}>
        <div className={styles.fieldAlert}>
          {alert && <div className={styles.alertCritical} />}
          {itemIcon}
        </div>
        <div className={styles.fieldTextRight}>
          {itemName}
          <div className={styles.fieldInfo}>
            {date?.toLocaleString(locale, format)}
          </div>
        </div>
        <div className={styles.open}/>
      </div>
    </>
  );
});

type ManageUpdatesHistoryProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
};
const ManageUpdatesHistory = observer(({ pangolin, domain }: ManageUpdatesHistoryProps) => {
  const styles = useStyles(themedStyles);

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  const key = (history: UpdateHistoryRef) => {
    switch (history.type) {
      case UpdateHistoryType.UPGRADE: return history.upgrade_id;
      case UpdateHistoryType.RESTORE: return history.restore_id;
      default: return UnreachableFunction(history);
    }
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>
        {Messages.historyTitle()}
      </div>
      <div className={styles.field}>
        <div className={styles.fieldPart}>
          {domain.updateHistory.map(history => (
            <ManageUpdateHistoryItem
              key={key(history)}
              pangolin={pangolin}
              domain={domain}
              item={history}
              locale={locale}
              format={format}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

type MoreSnapshotDeleteModalProps = MoreBaseModalProps;
const MoreSnapshotDeleteModal = observer(({ styles, domain, modal, root }: MoreSnapshotDeleteModalProps) => {
  const { id } = useParams<{ id: string }>();

  const snapshot = domain.updateSnapshots.get(id);

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  const [busy] = React.useState(new BusyStore());
  const [passwordStore] = React.useState(new PasswordStore(''));
  const [passwordError, setPasswordError] = React.useState(' ');

  const disabled = passwordStore.value.length === 0;

  const onSubmit = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();

    setPasswordError(' ');

    if (!snapshot) {
      setPasswordError(Messages.errorGeneric());
      return;
    }

    const result = await busy.perform(async() => {
      try {
        modal.freezeModal();
        return await UpdateSnapshotService.delete(
          domain, snapshot, passwordStore.value
        );
      } finally {
        modal.unfreezeModal();
      }
    });

    if (result.kind === ResultKind.OK) {
      runInAction(() => {
        while (modal.history.position > 1) modal.history.goBack();
        modal.history.replace(`${root}/snapshots`);
      });
    } else if (result.status === 401) {
      // Display authorization error
      setPasswordError(Messages.errorUnauthorized());
    } else {
      // Display generic error
      setPasswordError(Messages.errorGeneric());
    }
  }, [modal]);

  return (
    <div className={classNames(styles.modalContainerBig, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.moreSnapshotDeleteTitle()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <form className={styles.modalContentRow} onSubmit={onSubmit}>
          <div className={styles.hero}>
            <img className={styles.heroIcon} src={styles.heroIconImageWarning} alt='Warning Icon' />
            <div className={styles.heroTitle}>
              <ReactMarkdown>
                {Messages.moreSnapshotDeleteHeroTitle(
                  snapshot?.created_at.toLocaleString(locale, format) ?? '',
                  snapshot?.size ? ` (${bytesToSize(snapshot.size)})` : ''
                )}
              </ReactMarkdown>
            </div>
            <p className={styles.heroText}>
              {Messages.moreSnapshotDeleteHeroText()}
              <br/><br/>
              <strong>
                {Messages.moreSnapshotDeleteHeroWarning()}
              </strong>
            </p>
          </div>
          <LabeledInput label={Messages.moreSnapshotDeleteInputPassword()}>
            <PasswordInput
              store={passwordStore}
              name='password'
              id='password'
            />
          </LabeledInput>
          <ErrorMessage message={passwordError}/>
          <ActiveButton busy={busy} disabled={disabled}>
            {Messages.moreSnapshotDeleteButtonConfirm()}
          </ActiveButton>
          <DefaultButton type='button' disabled={busy.value} onClick={modal.history.goBack}>
            {Messages.moreSnapshotDeleteButtonBack()}
          </DefaultButton>
        </form>
      </StandardPage>
    </div>
  );
});

type MoreSnapshotInputCardProps = MoreBaseModalProps & { snapshot: UpdateSnapshot };
const MoreSnapshotInputCard = observer(({ styles, pangolin, domain, root, snapshot } : MoreSnapshotInputCardProps) => {
  const release = domain.updateReleases.get(snapshot.release_id);

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
  };

  const ongoing = useMemo(() => {
    return !!pangolin.updateActiveUpgradeID || !!pangolin.updateActiveRestoreID;
  }, [pangolin.updateActiveUpgradeID, pangolin.updateActiveRestoreID]);

  return (
    <div className={classNames(styles.field, styles.item)}>
      <img className={styles.fieldIcon} src={styles.fieldIconImageModified} alt='Snapshot Icon' />
      <div className={styles.fieldTextLeft} style={{ flex: 2 }}>
        {snapshot.created_at.toLocaleString(locale, format)}
        <span className={styles.fieldInfo}>
          {release?.tag}
        </span>
      </div>
      <div className={styles.fieldTextRight}>
        <span className={styles.fieldInfo}>
          {snapshot.size &&bytesToSize(snapshot.size)}
        </span>
        &nbsp;
      </div>
      {!ongoing ? (
        <Link to={`${root}/snapshots/${snapshot.id}/delete`}>
          <div className={styles.delete}/>
        </Link>
      ) : (
        <a className={styles.disabled}>
          <div className={styles.delete}/>
        </a>
      )}
    </div>
  );
});

type MoreSnapshotListModalProps = MoreBaseModalProps;
const MoreSnapshotListModal = observer((props: MoreSnapshotListModalProps) => {
  const { styles, modal, domain } = props;
  return (
    <div className={classNames(styles.modalContainerBig, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.moreSnapshotListTitle()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            {domain.updateSnapshots.map(snapshot => (
              <MoreSnapshotInputCard
                key={snapshot.id}
                snapshot={snapshot}
                {...props}
              />
            ))}
          </div>
          <DefaultButton type='button' onClick={modal.history.goBack}>
            {Messages.moreSnapshotListButtonBack()}
          </DefaultButton>
        </div>
      </StandardPage>
    </div>
  );
});

type MoreHomeModalProps = MoreBaseModalProps;
const MoreHomeModal = observer((props: MoreHomeModalProps) => {
  const { styles, modal, root } = props;
  return (
    <div className={classNames(styles.modalContainer, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.moreTitle()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            <Link className={classNames(styles.field, styles.item)} to={`${root}/snapshots`}>
              <img className={styles.fieldIcon} src={styles.fieldIconImageModified} alt='Snapshot Icon' />
              <div className={styles.fieldTextRight}>
                {Messages.moreChoiceSnapshotsName()}
                <div className={styles.fieldInfoLong}>
                  {Messages.moreChoiceSnapshotsInfo()}
                </div>
              </div>
              <div className={styles.open}/>
            </Link>
          </div>
          <DefaultButton type='button' onClick={modal.history.goBack}>
            {Messages.moreButtonBack()}
          </DefaultButton>
        </div>
      </StandardPage>
    </div>
  );
});

type MoreBaseModalProps = {
  styles: ManageUpdatesStyles;
  accounts: IAccountsDomain;
  pangolin: Pangolin;
  domain: IPangolinDomain;
  modal: HistoryModalStore;
  root: string;
};

type MoreModalProps = MoreBaseModalProps;

const MoreModal = observer((props: MoreModalProps) => {
  const { modal, root } = props;
  return (
    <Modal store={modal} onBackdropClick={modal.history.goBack}>
      <Router history={modal.history}>
        <Switch>
          <Route path={`${root}/home`}>
            <MoreHomeModal {...props} />
          </Route>
          <Route path={`${root}/snapshots/:id/delete`}>
            <MoreSnapshotDeleteModal {...props} />
          </Route>
          <Route path={`${root}/snapshots`}>
            <MoreSnapshotListModal {...props} />
          </Route>
        </Switch>
      </Router>
    </Modal>
  );
});

type BaseManageUpdatesProps = {
  accounts: IAccountsDomain;
  pangolin: Pangolin;
  domain: IPangolinDomain;
};
export const BaseManageUpdates = observer(({ accounts, pangolin, domain }: BaseManageUpdatesProps) => {
  const location = useLocation();
  const styles = useStyles(themedStyles);

  const root = `${location.pathname}/more`;

  const [modal] = React.useState(() => new HistoryModalStore(false));

  React.useEffect(() => { return modal.handleGoBack(); }, [modal]);

  const onShowModal = React.useCallback(() => modal.showModalAt(`${root}/home`), [modal, root]);

  const updateLatestRelease = (
    !domain.updateReleases.size ? undefined :
    domain.updateReleases.getAt(0)
  );
  const updateCurrentRelease = (
    !pangolin.updateActiveReleaseID ? undefined :
    domain.updateReleases.get(pangolin.updateActiveReleaseID)
  );

  const updateLatestTag = updateLatestRelease?.tag ?? null;
  const updateCurrentTag = updateCurrentRelease?.tag ?? pangolin.updateVersion ?? null;

  const upgrade = (
    !pangolin.updateActiveUpgradeID ? undefined :
    domain.updateUpgrades.get(pangolin.updateActiveUpgradeID)
  );

  const restore = (
    !pangolin.updateActiveRestoreID ? undefined :
    domain.updateRestores.get(pangolin.updateActiveRestoreID)
  );

  return (
    <>
      <MoreModal
        styles={styles}
        accounts={accounts}
        pangolin={pangolin}
        domain={domain}
        modal={modal}
        root={root}
      />
      <StandardPage>
        <div className={styles.header}>
            <div className={styles.headerItem}>
                <BackButton/>
                <div className={styles.headerText}>
                    {Messages.title()}
                </div>
            </div>
            <div className={styles.headerItem}>
              <button type='button' className={styles.headerButton}
                      onClick={onShowModal}>
                <div className={styles.headerButtonIcon}/>
              </button>
            </div>
        </div>
        <ManageUpdatesHero
          pangolin={pangolin}
          latest={updateLatestTag}
          current={updateCurrentTag}
        />
        <div className={styles.content}>
          {
            upgrade ? (<>
              <ManageUpdatesUpgradeInfo
                domain={domain}
                upgrade={upgrade}
              />
              <ManageUpdatesUpgradeProgress
                upgrade={upgrade}
              />
              <ManageUpdatesUpgradeActions
                pangolin={pangolin}
                domain={domain}
                upgrade={upgrade}
              />
            </>) :
            restore ? (<>
              <ManageUpdatesRestoreInfo
                domain={domain}
                restore={restore}
              />
              <ManageUpdatesRestoreProgress
                restore={restore}
              />
              <ManageUpdatesRestoreActions
                pangolin={pangolin}
                domain={domain}
                restore={restore}
              />
            </>) :
            (<>
              <ManageUpdatesDefaultInfo
                pangolin={pangolin}
                domain={domain}
              />
              <ManageUpdatesDefaultActions
                pangolin={pangolin}
                domain={domain}
              />
            </>)
          }
          <ManageUpdatesHistory
            pangolin={pangolin}
            domain={domain}
          />
        </div>
      </StandardPage>
    </>
  );
});

export const ManageUpdates = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  const domain = usePangolinDomain(pangolinID);
  if (!domain) return <Redirect />;

  return (
    <BaseManageUpdates
      accounts={accounts}
      pangolin={pangolin}
      domain={domain}
    />
  );
});
